import { formatDate } from "@clipboard-health/date-time";
import { Card, Divider, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";

import { useGetProfessionalReference } from "../api/useGetProfessionalReference";
import { PROFESSIONAL_REFERENCE_STATUS, PROFESSIONAL_SHARE_FLOW } from "../constants";
import { useShareProfessionalReference } from "../hooks/useShareProfessionalReference";
import { type ProfessionalReference } from "../types";
import { formatEmploymentPeriod } from "../utils/formatters";
import { ProfessionalReferencesStatusPill } from "./StatusPill";

export interface ReferenceCardProps {
  reference: ProfessionalReference;
  completed: boolean;
}

export function ReferenceCard({ reference, completed }: ReferenceCardProps) {
  const { showSuccessToast } = useToast();
  const isSharingEnabled =
    reference.status === PROFESSIONAL_REFERENCE_STATUS.REQUESTED && !completed;

  const { data: professionalReferenceData, isLoading: isLoadingProfessionalReference } =
    useGetProfessionalReference(reference._id, {
      enabled: isSharingEnabled, // We only need invite message when sharing
    });

  const { mutate: shareProfessionalReference, isLoading: isSharingProfessionalReference } =
    useShareProfessionalReference();

  const referenceFullName = [reference.referenceFirstName, reference.referenceLastName]
    .filter(Boolean)
    .join(" ");

  return (
    <Card
      outlined
      variant="tertiary"
      sx={{ p: 2 }}
      role="article"
      aria-label="Professional reference"
    >
      <CardContent>
        <Stack spacing={5}>
          {/* Reference status pill */}
          <ProfessionalReferencesStatusPill status={reference.status} />

          {/* Reference details */}
          <Text variant="h5">{referenceFullName}</Text>

          <Stack gap={1}>
            <Text variant="body2">Employer</Text>
            <Stack>
              <Text variant="h5">{reference.employerName}</Text>
              <Text variant="caption">
                {formatEmploymentPeriod(
                  reference.fromMonth,
                  reference.fromYear,
                  reference.toMonth,
                  reference.toYear
                )}
              </Text>
            </Stack>
          </Stack>

          {/* Once requirement is completed, we don't show the date sent */}
          {isDefined(reference.createdAt) && !completed && (
            <>
              <Divider />
              <Stack gap={1}>
                <Text variant="body2">Date Sent</Text>
                <Text variant="h5">{formatDate(reference.createdAt)}</Text>
              </Stack>
            </>
          )}

          {/* Share button for pending references */}
          {isSharingEnabled && (
            <Box width="100%" sx={{ pt: 2 }}>
              <LoadingButton
                fullWidth
                variant="outlined"
                isLoading={isSharingProfessionalReference}
                disabled={isLoadingProfessionalReference || !isDefined(professionalReferenceData)}
                onClick={() => {
                  if (!isDefined(professionalReferenceData)) {
                    return;
                  }

                  shareProfessionalReference(
                    { inviteMessage: professionalReferenceData.inviteMessage },
                    {
                      onSuccess: (shareResult) => {
                        logEvent(APP_V2_USER_EVENTS.PROFESSIONAL_REFERENCE_SHARED, {
                          referenceId: reference._id,
                          flow: PROFESSIONAL_SHARE_FLOW.REMIND,
                          ...(isDefined(shareResult) && { appName: shareResult.activityType }),
                        });
                        showSuccessToast("Reminder sent successfully.");
                      },
                    }
                  );
                }}
              >
                Send Reminder
              </LoadingButton>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
